import { logAmplitudeEvent } from 'amplitude/amplitude'
import { authSelectors } from 'ducks/auth'
import ProUXButton from 'elements/proUXButtons/ProUXButton'
import { useNotify, useTranslate } from 'ra-core'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import restClient from 'restClient'
import { AvailableFinanceIntegrationType } from 'types/orgs'
import { reloadEntireApp } from 'util/misc'

type PropTypes = {
  integration: AvailableFinanceIntegrationType
  isEnabled?: boolean
  onClick?: () => void
}

const DisableIntegrationButton: React.FC<PropTypes> = ({ integration, isEnabled, onClick }) => {
  const [disabling, setDisabling] = useState(false)
  const notify = useNotify()
  const translate = useTranslate()
  const isAdmin = useSelector(authSelectors.getIsAdmin)
  const isStaff = useSelector(authSelectors.getIsStaff)
  const orgId = useSelector(authSelectors.getOrgId)

  // At the moment only checkout finance can be disabled by either staff or org admins.
  const canDisable = isEnabled && integration.is_checkout_finance && (isAdmin || isStaff)

  if (!canDisable) return null

  const openDisableConfirm = () => {
    const confirmed = window.confirm(translate('Are you sure you want to disable this integration?'))
    if (!confirmed) return

    setDisabling(true)
    const restClientInstance = restClient(window.API_ROOT + '/api')
    restClientInstance('CUSTOM_POST', 'custom', {
      url: `orgs/${orgId}/disable_integration/${integration.integration_name}/`,
    })
      .then((response: any) => {
        if (response.data?.success) {
          logAmplitudeEvent('integrated_finance_account_disconnected', {
            integration: integration.integration_name,
          })
          onClick?.()
          const message = translate('%{partnerName} integration has been disabled.', {
            partnerName: integration.display_name,
          })
          const newUrl = `${window.location.origin}${window.location.pathname}?integration_disabled=1&partner=${encodeURIComponent(
            integration.integration_name
          )}&partner_display_name=${encodeURIComponent(integration.display_name)}&message=${encodeURIComponent(message)}`
          window.history.replaceState(null, '', newUrl)
          reloadEntireApp()
        } else {
          notify(response.data?.message || translate('Unable to disable integration'), 'warning')
        }
      })
      .catch((error: any) => {
        notify(error?.body?.message || translate('Unable to disable integration'), 'warning')
      })
      .finally(() => setDisabling(false))
  }

  return <ProUXButton label="Disable" onClick={openDisableConfirm} type="secondary" disabled={disabling} />
}

export default DisableIntegrationButton
